import React from 'react'

import {
  usePRFileArtboards,
  useRootNodeAbsoluteExtent,
  PRMarinaFileArtboard,
  PRMarinaLayerType,
  usePRFileSections,
} from '@sketch-hq/sketch-web-renderer'

import { EntityOverlay } from '../../../../canvas/components/EntityOverlay'
import { useInspectorContext } from '../../../FrameView'
import {
  useViewModeState,
  useViewModeOverlayProps,
  useViewModeGlobalEvents,
} from './viewMode.hooks'
import { useInspectModeOverlayProps } from './inspectMode.hooks'
import { Rect } from '../../../../inspector/types'
import { useCanvasAnnotations } from '../../../../annotations/hooks'
import { useCanvasInteractions } from '../../../../canvas/components/CanvasInteractions'

type PageCanvasFrameOverlayProps = {
  entity: PRMarinaFileArtboard
  isInspectorActive: boolean
  // View mode props
  framesRefs: React.RefObject<Record<string, HTMLDivElement | null>>
  selectedArtboardUUID: string | null
  setSelectedArtboardUUID: (uuid: string | null) => void
  rootExtent: Rect
  isPlacingDraftAnnotation: boolean
  disableInteractions?: boolean
}

const PageCanvasFrameOverlay = React.memo(function PageCanvasFrameOverlay({
  entity,
  isInspectorActive,
  framesRefs,
  selectedArtboardUUID,
  setSelectedArtboardUUID,
  rootExtent,
  isPlacingDraftAnnotation,
  disableInteractions,
}: PageCanvasFrameOverlayProps) {
  const node = entity.getNode()

  const isSelected = selectedArtboardUUID === entity.objectId
  const isHidden = node?.isEnabled() === false
  const isSection = node?.isSection()

  const viewModeProps = useViewModeOverlayProps(
    entity,
    selectedArtboardUUID,
    setSelectedArtboardUUID,
    isPlacingDraftAnnotation
  )
  const inspectModeProps = useInspectModeOverlayProps(entity)

  let overlayProps = viewModeProps
  if (disableInteractions || (isSection && !isInspectorActive)) {
    overlayProps = {
      disableAutoHover: true,
    }
  } else if (isInspectorActive) {
    overlayProps = inspectModeProps
  }

  // Hidden top-level frames should not render overlay decorations unless selected.
  if (isHidden && !isSelected) {
    return null
  }

  const isVariantSet = node ? node.getVariantSet().size() > 0 : false

  const isComponent =
    node?.getLayer()?.type.value === PRMarinaLayerType.SymbolMaster ||
    isVariantSet

  return (
    <EntityOverlay
      ref={(ref: HTMLDivElement | null) => {
        if (framesRefs.current) {
          framesRefs.current[entity.objectId] = ref
        }
      }}
      bounds={entity.bounds}
      artboardName={entity.name}
      artboardUUID={entity.objectId}
      isComponent={isComponent}
      rootExtent={rootExtent}
      {...overlayProps}
    />
  )
})

/**
 * The overlay we show on top ot the page canvas top level frames. The overlay is used to show
 * the artboards' names, cta, outline and listen for interactions on them.
 * Note: we use a unified component for both view modes to use only one React tree and
 * avoid remounting all overlays when switching modes.
 */
export const PageCanvasFramesOverlays = React.memo(
  function PageCanvasFramesOverlays() {
    const { isInspectorActive } = useInspectorContext()
    const topLevelFrames = usePRFileArtboards() ?? []
    const sections = usePRFileSections() ?? []

    const decoratedEntities = [...sections, ...topLevelFrames]

    //@TODO: We should move the selected frame terminology to selected entity/node
    const { framesRefs, selectedArtboardUUID, setSelectedArtboardUUID } =
      useViewModeState()

    const rootExtent = useRootNodeAbsoluteExtent()
    const { isPlacingDraftAnnotation } = useCanvasAnnotations()
    const { isEnabled: isCanvasInteractionsEnabled } = useCanvasInteractions()

    // View mode global events - only active in view mode
    useViewModeGlobalEvents(
      framesRefs,
      selectedArtboardUUID,
      setSelectedArtboardUUID,
      isInspectorActive
    )

    return (
      <>
        {decoratedEntities.map(entity => (
          <PageCanvasFrameOverlay
            key={entity.objectId}
            entity={entity}
            isInspectorActive={isInspectorActive}
            // View mode props
            framesRefs={framesRefs}
            selectedArtboardUUID={selectedArtboardUUID}
            setSelectedArtboardUUID={setSelectedArtboardUUID}
            rootExtent={rootExtent}
            isPlacingDraftAnnotation={isPlacingDraftAnnotation ?? false}
            disableInteractions={!isCanvasInteractionsEnabled}
          />
        ))}
      </>
    )
  }
)
